/**
 * What the projection says, in the order an analyst has to defend it: where the
 * balance ends up, who moves and why, who crosses the line, what we would be
 * conceding for nothing, and who has to be secured first.
 *
 * Every figure here is downstream of a stated assumption, and the page says so.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, CircleSlash, Info, ListOrdered, MoveRight, Repeat2, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, EmptyState, Pill, SectionHeader } from '../../../components/ui';
import type { BalanceOfForces, ProjectedMove, ScenarioProjection } from '../../../types/issues';
import { PositionChange } from '../components/PositionMark';
import { subjectHref } from '../components/format';
import { ForcesBar, IndexTrack } from '../map/PositionScale';
import { describeIndex, detailOf, formatIndex } from '../map/position';
import { propagationWords, sortMoves, summarise, summaryLine } from './scenarioModel';

/**
 * Show the bar where it started, then let it travel: the size of a move is the
 * thing being communicated, and a bar that simply appears hides it.
 */
function useTravelled(projection: ScenarioProjection | null, reducedMotion: boolean): BalanceOfForces | null {
  const [shown, setShown] = useState<BalanceOfForces | null>(projection?.after ?? null);
  const stamp = projection?.computedAt ?? null;
  useEffect(() => {
    if (!projection) {
      setShown(null);
      return;
    }
    if (reducedMotion) {
      setShown(projection.after);
      return;
    }
    setShown(projection.before);
    const frame = requestAnimationFrame(() => {
      window.setTimeout(() => setShown(projection.after), 60);
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp, reducedMotion]);
  return shown ?? projection?.after ?? null;
}

const MoveRow: React.FC<{ move: ProjectedMove }> = ({ move }) => (
  <li className="flex flex-col gap-1.5 px-4 py-2.5 sm:flex-row sm:items-start sm:gap-3">
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <a
          href={subjectHref(move.subject)}
          className="truncate text-[13px] text-ink transition-colors hover:text-primary-600"
        >
          {move.subject.label}
        </a>
        {move.flipped && (
          <Pill tone={move.delta > 0 ? 'green' : 'red'} title="Crosses the line between the camps">
            <Repeat2 className="h-3 w-3" strokeWidth={1.75} />
            {move.delta > 0 ? 'Flips to us' : 'Flips away'}
          </Pill>
        )}
        {!move.direct && (
          <Pill tone="neutral" title="Not named by any lever — this is a knock-on effect of the propagation assumption">
            Knock-on
          </Pill>
        )}
      </span>
      <span className="mt-1 block text-[12px] leading-relaxed text-neutral-500">{move.because}</span>
    </span>
    <span className="flex flex-shrink-0 flex-col items-start gap-1 sm:items-end">
      <PositionChange from={move.from} to={move.to} />
      {!Number.isInteger(move.to) && (
        <span className="text-[11px] tabular-nums text-neutral-400" title="Projected reading on the −2…+2 scale">
          {detailOf(move.to)}
        </span>
      )}
    </span>
  </li>
);

export const ProjectionView: React.FC<{
  projection: ScenarioProjection | null;
  /** A preview is in flight — keep the old picture, but say it is going stale */
  pending?: boolean;
  propagation: number;
  assumptions?: string | null;
  reducedMotion?: boolean;
  className?: string;
}> = ({ projection, pending = false, propagation, assumptions, reducedMotion = false, className = '' }) => {
  const shown = useTravelled(projection, reducedMotion);
  const summary = useMemo(() => summarise(projection), [projection]);
  const moves = useMemo(() => sortMoves(projection?.moves || []), [projection]);

  if (!projection) {
    return (
      <Card className={className}>
        <EmptyState icon={Info} title="Add a lever to see what it would do" />
      </Card>
    );
  }

  const gained = projection.flips?.gained || [];
  const lost = projection.flips?.lost || [];
  const sequence = projection.sequence || [];
  const ineffective = projection.ineffective || [];
  const maxLeverage = Math.max(1, ...sequence.map((s) => Math.abs(s.leverage || 0)));

  return (
    <div className={`space-y-4 ${pending ? 'opacity-70 transition-opacity' : 'transition-opacity'} ${className}`}>
      {/* ---------- where it ends up ---------- */}
      <Card padded>
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div>
            <p className="text-[12px] text-neutral-500">Balance if we do this</p>
            <p className="mt-1 flex flex-wrap items-baseline gap-2">
              <span className="text-[26px] leading-none tracking-[-0.02em] tabular-nums text-neutral-400">
                {formatIndex(projection.before?.index ?? 0)}
              </span>
              <ArrowRight className="h-4 w-4 self-center text-neutral-300" strokeWidth={1.75} />
              <span className="text-[26px] leading-none tracking-[-0.02em] tabular-nums text-ink">
                {formatIndex(projection.after?.index ?? 0)}
              </span>
              <span className="text-[13px] text-neutral-500">{describeIndex(projection.after?.index ?? 0)}</span>
            </p>
          </div>
          <Pill tone={summary.indexDelta > 0 ? 'green' : summary.indexDelta < 0 ? 'red' : 'neutral'}>
            {summary.indexDelta > 0 ? (
              <TrendingUp className="h-3 w-3" strokeWidth={1.75} />
            ) : summary.indexDelta < 0 ? (
              <TrendingDown className="h-3 w-3" strokeWidth={1.75} />
            ) : (
              <MoveRight className="h-3 w-3" strokeWidth={1.75} />
            )}
            {formatIndex(summary.indexDelta)} points
          </Pill>
        </div>

        <IndexTrack index={projection.after?.index ?? 0} before={projection.before?.index ?? 0} className="mt-3" />

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-3">
            <span className="w-11 flex-shrink-0 text-[11.5px] text-neutral-400">Before</span>
            <ForcesBar balance={projection.before} showKey={false} animate={false} className="flex-1" />
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-px w-11 flex-shrink-0 text-[11.5px] text-ink">After</span>
            <ForcesBar balance={shown} className="flex-1" />
          </div>
        </div>

        <p className="mt-3 border-t border-neutral-200/70 pt-3 text-[12px] text-neutral-500">{summaryLine(projection)}</p>
      </Card>

      {/* ---------- who moves ---------- */}
      <Card divided>
        <div className="px-4 pb-1 pt-3.5">
          <SectionHeader
            title="Projected moves"
            aside={<span>{moves.length ? `${moves.length} in total` : ''}</span>}
            className="mb-0"
          />
        </div>
        {moves.length === 0 ? (
          <EmptyState icon={CircleSlash} title="Nobody moves — the levers reach no one on this map" />
        ) : (
          <ul>
            {moves.map((move) => (
              <MoveRow key={`${move.subject.type}:${move.subject.id}`} move={move} />
            ))}
          </ul>
        )}
      </Card>

      {/* ---------- flips ---------- */}
      {(gained.length > 0 || lost.length > 0) && (
        <Card padded>
          <SectionHeader title="Who crosses the line" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-[12px] text-emerald-700">Gained ({gained.length})</p>
              {gained.length === 0 ? (
                <p className="text-[12px] text-neutral-400">Nobody</p>
              ) : (
                <ul className="space-y-1">
                  {gained.map((subject) => (
                    <li key={`${subject.type}:${subject.id}`} className="truncate text-[13px] text-ink">
                      {subject.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-[12px] text-primary-700">Lost ({lost.length})</p>
              {lost.length === 0 ? (
                <p className="text-[12px] text-neutral-400">Nobody</p>
              ) : (
                <ul className="space-y-1">
                  {lost.map((subject) => (
                    <li key={`${subject.type}:${subject.id}`} className="truncate text-[13px] text-ink">
                      {subject.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ---------- sequence ---------- */}
      <Card divided>
        <div className="px-4 pb-1 pt-3.5">
          <SectionHeader
            title="Secure first"
            aside={<span className="hidden sm:inline">Ranked by the weight their movement drags with them</span>}
            className="mb-0"
          />
        </div>
        {sequence.length === 0 ? (
          <EmptyState icon={ListOrdered} title="No order to recommend — nobody carries others here" />
        ) : (
          <ol>
            {sequence.map((step, index) => (
              <li key={`${step.subject.type}:${step.subject.id}`} className="flex items-start gap-3 px-4 py-2.5">
                <span className="mt-0.5 w-4 flex-shrink-0 text-[12px] tabular-nums text-neutral-400">{index + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-ink">{step.subject.label}</span>
                  <span className="mt-0.5 block text-[12px] leading-relaxed text-neutral-500">{step.reason}</span>
                </span>
                <span className="mt-1 flex w-20 flex-shrink-0 items-center gap-1.5" title={`Leverage ${Math.round(step.leverage)}`}>
                  <span className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-100">
                    <span
                      className="block h-full rounded-full bg-ink transition-[width] duration-500 ease-out motion-reduce:transition-none"
                      style={{ width: `${Math.max(4, (Math.abs(step.leverage || 0) / maxLeverage) * 100)}%` }}
                    />
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </Card>

      {/* ---------- what bought nothing ---------- */}
      {ineffective.length > 0 && (
        <Card padded>
          <SectionHeader
            title="Changed nothing"
            aside={<span className="hidden sm:inline">Worth knowing before conceding them</span>}
          />
          <ul className="flex flex-wrap gap-1.5">
            {ineffective.map((name) => (
              <li key={name}>
                <Pill tone="amber">{name}</Pill>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11.5px] text-neutral-400">
            These levers moved nobody on this map — either they reach actors we have not read, or the people they move were
            already there.
          </p>
        </Card>
      )}

      {/* ---------- the assumptions it rests on ---------- */}
      <div className="rounded-xl bg-neutral-50 px-3.5 py-3 text-[11.5px] leading-relaxed text-neutral-500">
        <p>
          <span className="text-neutral-600">This is a projection, not a forecast.</span> {propagationWords(propagation)}.
          Movement is applied to the positions on record; actors with no recorded position do not move at all.
        </p>
        {assumptions && <p className="mt-1.5">{assumptions}</p>}
        {projection.computedAt && (
          <p className="mt-1.5 text-neutral-400">
            Computed {new Date(projection.computedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
            {pending ? ' · updating…' : ''}
          </p>
        )}
      </div>
    </div>
  );
};
